import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreativeItem } from "@digital-twins/shared-types";
import type { AssistantImageRequest } from "@digital-twins/shared-types/assistant";
import { CreativeUsageWarning } from "@/components/creative-materials/CreativeUsageWarning";
import { canAllowExternalUse } from "@/components/creative-materials/creativeUsage";
import { creativeLibraryApi } from "@/lib/api/creative-library";

export interface QuoteUsageWarningsProps {
  requests: Array<AssistantImageRequest>;
}

/**
 * One line per material across the image set, from the frozen quote the output's label
 * inherits. Changing Allowed use affects the next quote, so this does not re-read usage.
 */
export function QuoteUsageWarnings(props: QuoteUsageWarningsProps) {
  const { requests } = props;

  const { t } = useTranslation("app");
  const [readable, setReadable] = useState<Array<CreativeItem>>([]);
  const warnings = Array.from(
    new Map(
      requests
        .flatMap((request) => request.contextSummary?.usageWarnings ?? [])
        .map((warning) => [warning.token ?? "", warning]),
    ).values(),
  );
  const key = JSON.stringify(
    warnings.flatMap((warning) => (warning.token ? [warning.token] : [])),
  );

  useEffect(() => {
    let isCurrent = true;
    const tokens: Array<string> = JSON.parse(key);
    void Promise.allSettled(
      tokens.map((token) => creativeLibraryApi.creative(token)),
    ).then((results) => {
      if (isCurrent) {
        setReadable(
          results.flatMap((result) =>
            result.status === "fulfilled" ? [result.value] : [],
          ),
        );
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [key]);

  if (!warnings.length) {
    return null;
  }
  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid="image-quote-usage-warnings"
    >
      {warnings.map((warning, index) => {
        const item = readable.find((row) => row.token === warning.token);
        return (
          <CreativeUsageWarning
            key={warning.token ?? "edit-source"}
            name={warning.name ?? t("creativeLibrary.usageWarning.editSource")}
            usage={warning.usage}
            allowedUseToken={
              item && canAllowExternalUse(item) ? item.token : null
            }
            testId={`image-quote-usage-warning-${index}`}
          />
        );
      })}
    </div>
  );
}
